import React, { useState } from 'react';
import api, { handleAPIError } from '../../services/api';

interface SerpItem {
  rank: number;
  title: string;
  url: string;
}

interface BriefSections {
  aiOverview: string | null;
  autocomplete: string[];
  paa: string[];
  discussions: string | null;
  serpImages: string[];
  related: string[];
  trends: string[];
  googleSerp: SerpItem[];
  bingSerp: SerpItem[];
}

interface AggregatedQuery {
  query: string;
  frequency: number;
  sources: string[];
}

interface BriefResponse {
  keyword: string;
  location: string;
  totalQueries: number;
  sources: { google: boolean; autocomplete: boolean; bing: boolean; trends: boolean };
  queries: AggregatedQuery[];
  sections: BriefSections;
}

const ContentBriefGenerator: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BriefResponse | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) return;

    setLoading(true);
    setError(null);
    setData(null);
    setExpandedSections({});

    try {
      const response = await api.post<BriefResponse>('/seotools/content-brief', {
        keyword: trimmedKeyword,
        location: location.trim() || undefined,
      });
      setData(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const sourceEntries = data
    ? [
        { key: 'google', label: 'Google SERP', available: data.sources.google },
        { key: 'autocomplete', label: 'Autocomplete', available: data.sources.autocomplete },
        { key: 'bing', label: 'Bing', available: data.sources.bing },
        { key: 'trends', label: 'Google Trends', available: data.sources.trends },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Content Brief Generator</h2>
        <p className="text-sm text-gray-500 mt-1">
          Generate a comprehensive content brief by aggregating data from multiple search sources. This makes 4 parallel API calls and may take 10-20 seconds.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="brief-keyword" className="block text-sm font-medium text-gray-700 mb-1">
              Keyword <span className="text-red-500">*</span>
            </label>
            <input
              id="brief-keyword"
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. best plumber in Austin"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
              required
            />
          </div>
          <div>
            <label htmlFor="brief-location" className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="brief-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Austin, Texas"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
        </div>
        <div>
          <button
            type="submit"
            disabled={loading || !keyword.trim()}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating Brief...
              </span>
            ) : (
              'Generate Brief'
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Header Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Keyword</span>
                <p className="text-sm font-semibold text-gray-800">{data.keyword}</p>
              </div>
              {data.location && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Location</span>
                  <p className="text-sm font-semibold text-gray-800">{data.location}</p>
                </div>
              )}
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Total Queries</span>
                <p className="text-sm font-semibold text-gray-800">{data.totalQueries}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {sourceEntries.map((src) => (
                <span
                  key={src.key}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                    src.available
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-gray-100 text-gray-500 border-gray-200'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${src.available ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {src.label}
                </span>
              ))}
            </div>
          </div>

          {/* Expandable Sections */}
          <div className="space-y-3">
            {/* AI Overview */}
            {data.sections.aiOverview && (
              <ExpandableCard
                title="AI Overview"
                isOpen={expandedSections['aiOverview'] ?? false}
                onToggle={() => toggleSection('aiOverview')}
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.sections.aiOverview}</p>
              </ExpandableCard>
            )}

            {/* People Also Ask */}
            {data.sections.paa.length > 0 && (
              <ExpandableCard
                title={`People Also Ask (${data.sections.paa.length})`}
                isOpen={expandedSections['paa'] ?? false}
                onToggle={() => toggleSection('paa')}
              >
                <ul className="space-y-1">
                  {data.sections.paa.map((q, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">-</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </ExpandableCard>
            )}

            {/* Google Autocomplete */}
            {data.sections.autocomplete.length > 0 && (
              <ExpandableCard
                title={`Google Autocomplete (${data.sections.autocomplete.length})`}
                isOpen={expandedSections['autocomplete'] ?? false}
                onToggle={() => toggleSection('autocomplete')}
              >
                <ul className="space-y-1">
                  {data.sections.autocomplete.map((q, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">-</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </ExpandableCard>
            )}

            {/* Related Searches */}
            {data.sections.related.length > 0 && (
              <ExpandableCard
                title={`Related Searches (${data.sections.related.length})`}
                isOpen={expandedSections['related'] ?? false}
                onToggle={() => toggleSection('related')}
              >
                <ul className="space-y-1">
                  {data.sections.related.map((q, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">-</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </ExpandableCard>
            )}

            {/* Google Trends */}
            {data.sections.trends.length > 0 && (
              <ExpandableCard
                title={`Google Trends (${data.sections.trends.length})`}
                isOpen={expandedSections['trends'] ?? false}
                onToggle={() => toggleSection('trends')}
              >
                <ul className="space-y-1">
                  {data.sections.trends.map((q, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">-</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </ExpandableCard>
            )}

            {/* Google SERP */}
            {data.sections.googleSerp.length > 0 && (
              <ExpandableCard
                title={`Top Google SERP (${data.sections.googleSerp.length})`}
                isOpen={expandedSections['googleSerp'] ?? false}
                onToggle={() => toggleSection('googleSerp')}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.sections.googleSerp.map((item, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-600 font-mono">{item.rank}</td>
                          <td className="px-3 py-2 text-sm text-gray-800">{item.title}</td>
                          <td className="px-3 py-2 text-sm text-blue-600 truncate max-w-xs">
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {item.url}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ExpandableCard>
            )}

            {/* Bing SERP */}
            {data.sections.bingSerp.length > 0 && (
              <ExpandableCard
                title={`Top Bing SERP (${data.sections.bingSerp.length})`}
                isOpen={expandedSections['bingSerp'] ?? false}
                onToggle={() => toggleSection('bingSerp')}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.sections.bingSerp.map((item, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-600 font-mono">{item.rank}</td>
                          <td className="px-3 py-2 text-sm text-gray-800">{item.title}</td>
                          <td className="px-3 py-2 text-sm text-blue-600 truncate max-w-xs">
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {item.url}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ExpandableCard>
            )}

            {/* Discussions */}
            {data.sections.discussions && (
              <ExpandableCard
                title="Discussions & Forums"
                isOpen={expandedSections['discussions'] ?? false}
                onToggle={() => toggleSection('discussions')}
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.sections.discussions}</p>
              </ExpandableCard>
            )}
          </div>

          {/* Aggregated Queries Table */}
          {data.queries.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Aggregated Queries ({data.queries.length})</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Query</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sources</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.queries.map((q, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2 text-sm text-gray-900">{q.query}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 font-mono">{q.frequency}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {q.sources.map((src) => (
                              <span
                                key={src}
                                className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                              >
                                {src}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* Reusable expandable card sub-component */
interface ExpandableCardProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({ title, isOpen, onToggle, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
    >
      <span className="text-sm font-medium text-gray-800">{title}</span>
      <svg
        className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && <div className="px-4 py-3 bg-white">{children}</div>}
  </div>
);

export default ContentBriefGenerator;
